import { BehaviorSubject } from 'rxjs';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { UnitFilterDictionary } from 'src/app/shared/models/unit-filter-dictionary.model';
import { PhoneNumberCountryStatus } from 'src/lib/fliko/helpers/phone-number.helper';
import { TemplateService } from 'src/app/shared/services/template-service/template.service';
import { ResidentUnitsVM } from 'src/app/view/users/services/models/users-view-models';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { DataTableConfig } from '../../clickable-row-table/models/data-table-config.models';
import { ReceiverSelectFilters } from '../view-models/receivers-select.view-model';
import { ReceiversTableRowsBase } from '../view-models/receivers-select-table.model';

export interface ReceiversSelectContentSubmitData {
  selectedResidentIds: Set<string>;
  visibleUnavailableResidentIds: Set<string>;
  filters: ReceiverSelectFilters;
}

@Component({
  selector: 'app-receivers-select-content[tableConfig][allFilters][residents][vmToTableRows]',
  templateUrl: './receivers-select-content.component.html',
  styleUrls: ['./receivers-select-content.component.scss']
})
export class ReceiversSelectContentComponent<T extends ReceiversTableRowsBase> implements OnInit, OnChanges {
  @Input() tableConfig!: DataTableConfig;
  @Input() allFilters!: UnitFilterDictionary;
  @Input() previousFilters?: ReceiverSelectFilters;
  @Input() residents!: ResidentUnitsVM[];
  @Input() defaultSelected?: Set<string>;
  @Input() phoneStatuses?: ReadonlyMap<string, PhoneNumberCountryStatus>;
  @Input() vmToTableRows!: (
    data: ResidentUnitsVM[],
    onClick: (id: string, value: boolean) => void,
    estateType: EstateType,
    selected: Set<string>,
    phoneStatuses?: ReadonlyMap<string, PhoneNumberCountryStatus>
  ) => T[];

  public tableData: T[] = [];
  public searchVal = '';
  public tabPaginator: BehaviorSubject<number> = new BehaviorSubject(0);

  public selectedResidents = new Set<string>();

  public get selectableRowCount(): number {
    return this.tableData.filter((row) => !row.checkbox.disabled).length;
  }

  public get selectedSelectableRowCount(): number {
    return this.tableData.filter((row) => !row.checkbox.disabled && this.selectedResidents.has(row.id.value)).length;
  }

  public get initFilters() {
    return (
      this.previousFilters ?? {
        types: [ResidentType.LANDLORD, ResidentType.TENANT],
        unitTypes: Object.values(RentalUnitType),
        ...UnitFilterDictionary.empty()
      }
    );
  }

  private savedFilters: ReceiverSelectFilters = this.initFilters;

  constructor(
    public templateService: TemplateService,
    private ctxDS: ContextDataService
  ) {}

  ngOnInit(): void {
    this.selectedResidents = new Set(this.defaultSelected);
    this.updateTableData(this.residents);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.residents) this.updateTableData(this.residents);
  }

  public onFiltersChanged(data: ReceiverSelectFilters) {
    this.savedFilters = data;
    const checkAddress = (val: string, adr: string[], numbers: boolean = false) =>
      adr.find((a) => a.includes(`&${val}${numbers ? '&' : '|'}`));
    const filtered = this.residents.filter((resUnit) => {
      const resident = data.types.includes(resUnit.type);
      const buildings = data.buildings.length > 0 ? data.buildings.find((x) => checkAddress(x.value, resUnit.addresses)) : true;
      const staircases =
        data.staircases.length > 0 ? data.staircases.find((x) => checkAddress(x.value, resUnit.addresses)) : true;
      const numbers = data.numbers.length > 0 ? data.numbers.find((x) => checkAddress(x.value, resUnit.addresses, true)) : true;

      let units = false;
      resUnit.units.forEach((unit) => {
        if (data.unitTypes.includes(unit.type)) {
          units = true;
          return;
        }
      });

      return units && resident && buildings && staircases && numbers;
    });
    this.updateTableData(filtered);
  }

  public onClick(id: string, value: boolean): void {
    if (value) this.selectedResidents.add(id);
    else this.selectedResidents.delete(id);
  }

  public checkAll(value: boolean): void {
    this.tableData.forEach((row) => row.checkbox.click(value));
  }

  public onSubmit(): ReceiversSelectContentSubmitData {
    // * Remove selections hidden by filters or unavailable for this action
    const selectableFilteredIds = new Set(this.tableData.filter((row) => !row.checkbox.disabled).map((row) => row.id.value));
    const visibleUnavailableResidentIds = new Set(
      this.tableData.filter((row) => row.checkbox.disabled).map((row) => row.id.value)
    );
    const filteredSelected = new Set(this.selectedResidents);

    this.selectedResidents.forEach((id) => {
      if (!selectableFilteredIds.has(id)) {
        filteredSelected.delete(id);
      }
    });

    return {
      selectedResidentIds: filteredSelected,
      visibleUnavailableResidentIds,
      filters: this.savedFilters
    };
  }

  private updateTableData(data: ResidentUnitsVM[]): void {
    this.tableData = this.vmToTableRows(
      data,
      (id: string, value: boolean) => this.onClick(id, value),
      this.ctxDS.peekCurrentEstate()!.type,
      this.selectedResidents,
      this.phoneStatuses
    );
  }
}
